import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck } from 'lucide-react';
import {
  Card,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Badge } from '@avagency/frontend/components/ui/badge';
import { Button } from '@avagency/frontend/components/ui/button';
import { Input } from '@avagency/frontend/components/ui/input';
import { Label } from '@avagency/frontend/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avagency/frontend/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@avagency/frontend/components/ui/dialog';
import { EmptyState } from '@avagency/frontend/components/shell/EmptyState';
import { ErrorState } from '@avagency/frontend/components/shell/ErrorState';
import { LoadingState } from '@avagency/frontend/components/shell/LoadingState';
import {
  fetchSuppliers,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  type Supplier,
  type SupplierCreateInput,
  type SupplierUpdateInput,
} from '../api';

type Modal =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; supplier: Supplier }
  | { kind: 'delete'; supplier: Supplier };

const PAYMENT_TERMS = ['Net 30', 'Net 60', 'Net 15', 'COD', 'Prepaid'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD'];

export function SupplierList() {
  const qc = useQueryClient();
  const [modal, setModal] = useState<Modal>({ kind: 'none' });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['popilot', 'suppliers'],
    queryFn: () => fetchSuppliers(),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['popilot', 'suppliers'] });

  const createMut = useMutation({
    mutationFn: createSupplier,
    onSuccess: () => {
      invalidate();
      setModal({ kind: 'none' });
    },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: SupplierUpdateInput }) =>
      updateSupplier(id, patch),
    onSuccess: () => {
      invalidate();
      setModal({ kind: 'none' });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => {
      invalidate();
      setModal({ kind: 'none' });
    },
  });

  if (isLoading) return <LoadingState rows={5} />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const suppliers = data?.suppliers ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Suppliers</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Vendors PO Pilot can issue purchase orders to. Tenant-wide suppliers
            are visible to all your stores; store-scoped ones are limited to one.
          </p>
        </div>
        <Button onClick={() => setModal({ kind: 'create' })}>
          <Plus className="mr-1 h-4 w-4" />
          Add Supplier
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {suppliers.length === 0 ? (
            <EmptyState
              icon={<Truck className="h-10 w-10" />}
              title="No suppliers yet"
              description="Add your first supplier to get started."
              action={
                <Button onClick={() => setModal({ kind: 'create' })}>
                  <Plus className="mr-1 h-4 w-4" />
                  Add Supplier
                </Button>
              }
            />
          ) : (
            <div className="rounded-md border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Terms</TableHead>
                    <TableHead>Currency</TableHead>
                    <TableHead className="text-right">Score</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {suppliers.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-muted-foreground">{s.email}</TableCell>
                      <TableCell>{s.default_payment_terms}</TableCell>
                      <TableCell>{s.default_currency}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {s.performance_score != null
                          ? s.performance_score.toFixed(1)
                          : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {s.integration_id
                          ? <Badge variant="outline" className="font-mono">store</Badge>
                          : <Badge variant="secondary">tenant-wide</Badge>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setModal({ kind: 'edit', supplier: s })}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setModal({ kind: 'delete', supplier: s })}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {modal.kind === 'create' && (
        <CreateSupplierDialog
          onClose={() => setModal({ kind: 'none' })}
          onSubmit={(input) => createMut.mutate(input)}
          isPending={createMut.isPending}
          error={createMut.error ? (createMut.error as Error).message : undefined}
        />
      )}

      {modal.kind === 'edit' && (
        <EditSupplierDialog
          supplier={modal.supplier}
          onClose={() => setModal({ kind: 'none' })}
          onSubmit={(patch) =>
            updateMut.mutate({ id: modal.supplier.id, patch })
          }
          isPending={updateMut.isPending}
          error={updateMut.error ? (updateMut.error as Error).message : undefined}
        />
      )}

      {modal.kind === 'delete' && (
        <DeleteDialog
          supplier={modal.supplier}
          onClose={() => setModal({ kind: 'none' })}
          onConfirm={() => deleteMut.mutate(modal.supplier.id)}
          isPending={deleteMut.isPending}
          error={deleteMut.error ? (deleteMut.error as Error).message : undefined}
        />
      )}
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────

function CreateSupplierDialog({
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  onClose: () => void;
  onSubmit: (input: SupplierCreateInput) => void;
  isPending: boolean;
  error?: string;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [terms, setTerms] = useState('Net 30');
  const [currency, setCurrency] = useState('USD');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      email,
      default_payment_terms: terms,
      default_currency: currency,
    });
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Add Supplier</DialogTitle>
          </DialogHeader>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <SupplierFields
            name={name}
            email={email}
            terms={terms}
            currency={currency}
            setName={setName}
            setEmail={setEmail}
            setTerms={setTerms}
            setCurrency={setCurrency}
            requireNameEmail
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} aria-busy={isPending}>
              {isPending ? 'Saving…' : 'Add Supplier'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit dialog ────────────────────────────────────────────────────

function EditSupplierDialog({
  supplier,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  supplier: Supplier;
  onClose: () => void;
  onSubmit: (patch: SupplierUpdateInput) => void;
  isPending: boolean;
  error?: string;
}) {
  const [name, setName] = useState(supplier.name);
  const [email, setEmail] = useState(supplier.email);
  const [terms, setTerms] = useState(supplier.default_payment_terms);
  const [currency, setCurrency] = useState(supplier.default_currency);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // Only send changed fields so we don't unintentionally clobber
    // server-side defaults the form doesn't expose.
    const patch: SupplierUpdateInput = {};
    if (name !== supplier.name) patch.name = name;
    if (email !== supplier.email) patch.email = email;
    if (terms !== supplier.default_payment_terms) patch.default_payment_terms = terms;
    if (currency !== supplier.default_currency) patch.default_currency = currency;
    onSubmit(patch);
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Edit Supplier</DialogTitle>
          </DialogHeader>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <SupplierFields
            name={name}
            email={email}
            terms={terms}
            currency={currency}
            setName={setName}
            setEmail={setEmail}
            setTerms={setTerms}
            setCurrency={setCurrency}
          />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending} aria-busy={isPending}>
              {isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Shared form fields ─────────────────────────────────────────────

function SupplierFields({
  name,
  email,
  terms,
  currency,
  setName,
  setEmail,
  setTerms,
  setCurrency,
  requireNameEmail,
}: {
  name: string;
  email: string;
  terms: string;
  currency: string;
  setName: (v: string) => void;
  setEmail: (v: string) => void;
  setTerms: (v: string) => void;
  setCurrency: (v: string) => void;
  requireNameEmail?: boolean;
}) {
  const selectClass =
    'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label htmlFor="supp-name">Name {requireNameEmail && '*'}</Label>
        <Input
          id="supp-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="supp-email">Email {requireNameEmail && '*'}</Label>
        <Input
          id="supp-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="supp-terms">Payment Terms</Label>
        <select
          id="supp-terms"
          value={terms}
          onChange={(e) => setTerms(e.target.value)}
          className={selectClass}
        >
          {PAYMENT_TERMS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </div>
      <div className="space-y-1">
        <Label htmlFor="supp-currency">Default Currency</Label>
        <select
          id="supp-currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          className={selectClass}
        >
          {CURRENCIES.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ─── Delete confirm dialog ─────────────────────────────────────────

function DeleteDialog({
  supplier,
  onClose,
  onConfirm,
  isPending,
  error,
}: {
  supplier: Supplier;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && !isPending && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete supplier?</DialogTitle>
          <DialogDescription>
            Delete <strong>{supplier.name}</strong>? This cannot be undone.
            Suppliers referenced by existing purchase orders cannot be deleted.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending} aria-busy={isPending}>
            {isPending ? 'Deleting…' : 'Yes, Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
